<template>
  <component :is="layout">
    <router-view />

    <!-- Loading Overlay -->
    <div
      v-if="isLoading"
      class="loading-overlay"
    >
      <div class="text-center">
        <div class="spinner spinner-lg mb-3" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
        <div class="text-primary fw-medium">Cargando sistema...</div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div class="position-fixed top-0 end-0 p-3" style="z-index: 9999">
      <div
        v-for="notification in notifications"
        :key="notification.id"
        class="alert mb-3 shadow-lg"
        :class="`alert-${notification.type}`"
        style="min-width: 300px; animation: slideInRight 0.3s ease;"
      >
        <div class="d-flex justify-content-between align-items-start">
          <div class="d-flex align-items-center">
            <i class="bi" :class="getNotificationIcon(notification.type) + ' me-2'"></i>
            <div>
              <strong class="d-block">{{ getNotificationTitle(notification.type) }}</strong>
              <span>{{ notification.message }}</span>
            </div>
          </div>
          <button
            type="button"
            class="btn-close btn-close-white ms-3"
            @click="removeNotification(notification.id)"
          ></button>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useNotificationStore } from './stores/notifications'
import { useThemeStore } from './stores/theme'
import DefaultLayout from './layouts/DefaultLayout.vue'
import BlankLayout from './layouts/BlankLayout.vue'

export default {
  name: 'App',
  components: {
    DefaultLayout,
    BlankLayout
  },
  setup() {
    const route = useRoute()
    const isLoading = ref(false)
    const notificationStore = useNotificationStore()
    const themeStore = useThemeStore()

    // Determine which layout to use based on route meta
    const layout = computed(() => {
      return route.meta.layout || 'DefaultLayout'
    })

    // Get notification icon
    const getNotificationIcon = (type) => {
      const icons = {
        success: 'bi-check-circle-fill text-success',
        error: 'bi-exclamation-triangle-fill text-danger',
        warning: 'bi-exclamation-circle-fill text-warning',
        info: 'bi-info-circle-fill text-info'
      }
      return icons[type] || icons.info
    }

    // Get notification title
    const getNotificationTitle = (type) => {
      const titles = {
        success: 'Éxito',
        error: 'Error',
        warning: 'Advertencia',
        info: 'Información'
      }
      return titles[type] || titles.info
    }

    // Remove notification
    const removeNotification = (id) => {
      notificationStore.removeNotification(id)
    }

    onMounted(() => {
      // Initialize theme system
      themeStore.initializeTheme()
      themeStore.setupSystemThemeListener()
    })

    return {
      layout,
      isLoading,
      notifications: notificationStore.notifications,
      getNotificationIcon,
      getNotificationTitle,
      removeNotification
    }
  }
}
</script>

<style lang="scss">
/* Import main styles */
@import './assets/styles/main.scss';

/* Custom notification animations */
@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Enhanced notifications */
.btn-close-white {
  background: none;
  border: none;
  color: currentColor;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
}

/* Loading overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
</style>